import { Injectable } from "@angular/core";
import { HttpClient } from "../shared/services/http/http-client.service";
import { HttpClient as RawHttpClient } from "@angular/common/http";
import { Router, ActivatedRouteSnapshot } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { BehaviorSubject, Observable, firstValueFrom } from "rxjs";

@Injectable()
export class authenticationService {
  constructor(
    private router: Router,
    private http: HttpClient,
    private toastr: ToastrService,
    private rawHttp: RawHttpClient
  ) {}

  private usernameSubject = new BehaviorSubject<string>(this.getUsername());

  async login(user) {
    try {
      const credentials = { user: user.email, password: user.password };
      const response = (await firstValueFrom(
        this.http.post("/api/auth/login", credentials)
      )) as any;

      this.setUsername(response.username);
      localStorage.setItem("permissions", JSON.stringify(response.permissions));
      this.permissionSet = null;
      this.router.navigate(["/home"]);
      return user;
    } catch (error) {
      throw new Error(error.message || "Server Auth Error");
    }
  }

  logout() {
    this.http.post("/api/auth/logout", {}).subscribe({
      next: () => this.completeLogout(),
      error: () => this.completeLogout(),
    });
  }

  /**
   * Removes the locally cached auth state. The route guard (and every
   * hasPermission check) reads these keys, so they must be cleared on logout.
   * Otherwise the guard still treats the user as authenticated and routes
   * such as /home render instead of redirecting to the login page.
   */
  clearAuthState(): void {
    localStorage.removeItem("username");
    localStorage.removeItem("permissions");
    this.permissionSet = null;
    this.usernameSubject.next(null);
  }

  /**
   * Probes the server for the state of the auth cookie. The cookie is HttpOnly,
   * so this is the only way to notice that a session has expired and that the
   * cached username/permissions in localStorage are no longer valid.
   */
  private async checkSession(): Promise<boolean> {
    try {
      await firstValueFrom(this.rawHttp.get("/api/auth/check"));
      return true;
    } catch (error) {
      // 401 means the cookie is gone or expired. Anything else (offline, 5xx)
      // is inconclusive, so don't lock the user out.
      return (error as any)?.status !== 401;
    }
  }

  private redirectToLogin(): void {
    this.toastr.warning("Login is required", "Alert!", {
      closeButton: true,
      enableHtml: true,
    });
    this.router.navigate(["/login"]);
  }

  private completeLogout(): void {
    this.clearAuthState();
    this.router.navigate(["/login"]);
  }

  startReset(username) {
    return this.http.post("/api/auth/reset", { username });
  }

  async reset(form) {
    try {
      (await this.http.patch("/api/auth/reset", form).toPromise()) as any;
      this.toastr.success("Login with your new password.", "Success!", {
        closeButton: true,
        enableHtml: true,
      });
      this.logout();
    } catch (error) {
      this.toastr.warning(error.message || "Server Error", "Alert!", {
        closeButton: true,
        enableHtml: true,
      });
    }
  }

  getUsername() {
    return localStorage.getItem("username");
  }

  setUsername(username: string): void {
    localStorage.setItem("username", username);
    this.usernameSubject.next(username);
  }

  getUsernameObservable(): Observable<string> {
    return this.usernameSubject.asObservable();
  }

  /**
   * hasPermission is called from templates, so it runs on every change detection
   * pass. Reading localStorage and JSON parsing the list on each call is slow
   * enough to show up as a hot spot in profiles, so the parsed list is cached
   * and only rebuilt when the stored permissions change (login / logout).
   */
  private permissionSet: Set<string> | null = null;

  private getPermissionSet(): Set<string> {
    if (this.permissionSet === null) {
      try {
        const stored = JSON.parse(localStorage.getItem("permissions"));
        this.permissionSet = new Set(Array.isArray(stored) ? stored : []);
      } catch {
        this.permissionSet = new Set();
      }
    }
    return this.permissionSet;
  }

  hasPermission(permission: string): boolean {
    return this.getPermissionSet().has(permission);
  }

  async canActivate(route: ActivatedRouteSnapshot): Promise<boolean> {
    if (!localStorage.getItem("username") || !localStorage.getItem("permissions")) {
      this.redirectToLogin();
      return false;
    }

    // localStorage outlives the auth cookie, so confirm the session is still
    // valid before rendering a guarded route such as /home.
    if (!(await this.checkSession())) {
      this.clearAuthState();
      this.redirectToLogin();
      return false;
    }

    if (route.data.permission) {
      // Support both single permission string and array of permissions
      const requiredPermissions = Array.isArray(route.data.permission)
        ? route.data.permission
        : [route.data.permission];

      // Check if user has at least one of the required permissions
      const hasRequiredPermission = requiredPermissions.some((permission) =>
        this.hasPermission(permission)
      );

      if (!hasRequiredPermission) {
        console.log("Insufficient permissions, need:", requiredPermissions);
        this.toastr.warning("Insufficient permissions", "Alert!", {
          closeButton: true,
          enableHtml: true,
        });

        this.router.navigate(["/home"]);
        return false;
      }
    }
    return true;
  }
}
